import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { AssetDetailsAPI } from "../viewmodel/AssetDetailsAPI";

// seconds -> "dd/mm/yyyy, hh:mm am/pm"
const formatDateTime = (value) => {
  if (!value || isNaN(Number(value))) return "-";
  return new Date(Number(value) * 1000).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// "2029-10-15" -> "15/10/2029"; other values unchanged; empty -> (empty)
const showValue = (value) => {
  if (value === null || value === undefined || value === "") return "(empty)";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : value;
};

// "Edit History" section on the asset details page: who changed what
const EditHistorySection = ({ assetId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setRows(await AssetDetailsAPI.fetchEditHistory(assetId));
      } catch (error) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    if (assetId) load();
  }, [assetId]);

  return (
    <Box
      sx={{
        border: "1px solid #3CB043",
        borderRadius: "1.2em 1.2em 1em 1em",
        overflow: "hidden",
        mt: "1em",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#3CB043",
          color: "#fff",
          p: ".5em 1em",
          minHeight: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <span style={{ fontSize: "1.1em" }}>Edit History</span>
        <IconButton
          onClick={() => setExpanded(!expanded)}
          sx={{ position: "absolute", right: "0.5em", color: "white" }}
        >
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      {expanded &&
        (loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ p: 2, textAlign: "center", color: "#666", fontSize: "0.9em" }}>
            No changes recorded yet
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto", maxHeight: 360 }}>
            <Table size="small" stickyHeader sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  {["When", "Who", "What changed", "Old → New", "From"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f2f8f2",
                        color: "#2b6b2f",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(r.changed_at)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.changed_by || "-"}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.field_label}</TableCell>
                    <TableCell>
                      <span style={{ color: "#888", textDecoration: "line-through" }}>
                        {showValue(r.old_value)}
                      </span>
                      {"  →  "}
                      <span style={{ color: "#052660", fontWeight: 600 }}>
                        {showValue(r.new_value)}
                      </span>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.8em", color: "#555" }}>
                      {r.source || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}
    </Box>
  );
};

export default EditHistorySection;